const mergeLogs = (existingLogs, newLogs) => [...newLogs, ...existingLogs];

const deduplicateLogsById = (logs) => {
  const logsById = logs.reduce((acc, log) => {
    const key = log.log_id ?? log.id;
    acc[key] = log;
    return acc;
  }, {});
  return Object.values(logsById);
};

function mapLogsByFlowId(flowNodeId, logs) {
  return {
    [flowNodeId]: logs,
  };
}

type MetaType = {
  pagination: {
    total: number;
    size: number;
    next_page_values: {
      timestamp: number;
      log_id: string;
    } | null;
  };
};

const extractPaginationState = (meta: MetaType, totalOfloadedLogs: number) => {
  const noNextPageValues = meta.pagination.next_page_values === null;
  const hasMoreValidation = !noNextPageValues && totalOfloadedLogs < meta.pagination.total;

  return {
    total: meta.pagination.total,
    size: meta.pagination.size,
    hasMore: hasMoreValidation,
    nextPageValues: meta.pagination.next_page_values,
  };
};

type ActionType = {
  type: 'GET_FLOW_RUN_LOGS_WITH_FACETS_SUCCESS_V2';
  entities: null;
  result: {
    status: number;
    message: string;
    logs: {
      data: any[];
      meta: {
        pagination: {
          total: number;
          size: number;
          next_page_values: {
            timestamp: number;
            log_id: string;
          } | null;
        };
        facets?: Record<string, any>;
      };
    };
  };
  receivedAt: number;
  requestInfo: {
    flowNodeId: number;
    sortOrder?: 'asc' | 'desc';
  };
};

type AutoRefreshActionType = {
  type: 'GET_FLOW_RUN_LOGS_WITH_FACETS_AUTO_REFRESH_SUCCESS_V2';
  entities: null;
  result: {
    status: number;
    message: string;
    logs: {
      data: any[];
      meta: {
        pagination: {
          total: number;
          size: number;
          next_page_values: {
            timestamp: number;
            log_id: string;
          } | null;
        };
        facets?: Record<string, any>;
      };
    };
  };
  receivedAt: number;
  requestInfo: {
    flowNodeId: number;
    sortOrder?: 'asc' | 'desc';
  };
};

/**
 * @FacadeFunction
 */
export function handleFlowRunLogsWithFacets(action: ActionType, state: any) {
  try {
    const response = action.result;
    const flowNodeId = action.requestInfo?.flowNodeId;
    const {
      logs: { meta, data: newLogs },
    } = response;

    const existingLogs = state.logs[flowNodeId] ?? [];
    const mergedLogs = mergeLogs(existingLogs, newLogs);
    const uniqueLogs = deduplicateLogsById(mergedLogs);
    const logsMappedByFlowId = mapLogsByFlowId(flowNodeId, uniqueLogs);

    const paginationState = extractPaginationState(meta, uniqueLogs.length);

    return {
      ...state,
      logs: logsMappedByFlowId,
      ...paginationState,
      facets: meta.facets ?? {},
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error('Error handling flow run logs with facets: ', error);
    return {
      ...state,
      isLoading: false,
      error: error,
    };
  }
}

export function handleFlowRunLogsWithFacetsAutoRefresh(action: AutoRefreshActionType, state: any) {
  try {
    const response = action.result;
    const flowNodeId = action.requestInfo?.flowNodeId;
    const {
      logs: { meta, data: newLogs },
    } = response;

    const existingLogs = state.logs[flowNodeId] ?? [];
    const mergedLogs = mergeLogs(existingLogs, newLogs);
    const uniqueLogs = deduplicateLogsById(mergedLogs);
    const logsMappedByFlowId = mapLogsByFlowId(flowNodeId, uniqueLogs);

    // For auto-refresh, preserve existing nextPageValues to maintain pagination context
    // Only update total, size, hasMore, and facets from the response
    const noNextPageValues = meta.pagination.next_page_values === null;
    const hasMoreValidation = !noNextPageValues && uniqueLogs.length < meta.pagination.total;

    return {
      ...state,
      logs: logsMappedByFlowId,
      total: meta.pagination.total,
      size: meta.pagination.size,
      hasMore: hasMoreValidation,
      nextPageValues: state.nextPageValues, // Preserve existing nextPageValues instead of updating from response
      facets: meta.facets ?? {},
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error('Error handling flow run logs with facets auto-refresh: ', error);
    return {
      ...state,
      isLoading: false,
      error: error,
    };
  }
}
